'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsBackpackingBannerTapped from '@alltrails/amplitude/events/logTrailDetailsBackpackingBannerTapped';
import useUser from '@alltrails/context/hooks/useUser';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import useUpsellContent from '@alltrails/upsell/hooks/useUpsellContent';
import styles from './BackpackingCta.module.scss';
import backpackingCtaImage from './backpackingCtaGraphic.avif';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const GenericPeakUpsell = dynamic(() => import('@alltrails/upsell/components/UpsellSystem/GenericPeakUpsell/GenericPeakUpsell'), { ssr: false });

type Props = {
  trailId: number;
  trailPageType: TrailPageType;
  trailSlug: string;
};

const BackpackingCta = ({ trailId, trailPageType, trailSlug }: Props) => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const {
    isOpen: isPeakUpsellOpen,
    serverUpsellContent,
    setIsOpen: setIsPeakUpsellOpen
  } = useUpsellContent('trail details backpacking banner tapped');

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const currentPath = window.location.pathname + window.location.search;
    return encodeURIComponent(currentPath);
  }, []);

  const navigateToCustomRoute = useCallback(() => {
    window.location.assign(wrapUrlSafe(`/explore/custom-routes/trail/${trailSlug}${returnTo ? `?returnTo=${returnTo}` : ''}`, languageRegionCode));
  }, [languageRegionCode, trailSlug, returnTo]);

  const onClick = useCallback(() => {
    logTrailDetailsBackpackingBannerTapped({
      member_tier: user?.subscriptionTier === 'base' ? 'free' : user?.subscriptionTier,
      trail_id: trailId,
      trail_page_type: trailPageType
    });
    if (!user) {
      setIsSignUpModalOpen(true);
    } else if (user.featureEntitlements?.includes('build_your_own_trail')) {
      navigateToCustomRoute();
    } else {
      setIsPeakUpsellOpen(true);
    }
  }, [navigateToCustomRoute, setIsPeakUpsellOpen, trailId, trailPageType, user]);

  return (
    <>
      <button className={styles.cta} onClick={onClick}>
        <Image src={backpackingCtaImage.src} alt="" width={52} height={52} />
        <div className={styles.ctaText}>
          <Typography component="div" variant="text150bold">
            <FormattedMessage defaultMessage="Going backpacking?" />
          </Typography>
          <Typography component="div" variant="text100" color="secondary">
            <FormattedMessage defaultMessage="Make a plan with Route Assistant" />
          </Typography>
        </div>
        <ChevronSm orientation="right" />
      </button>
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={newUser => {
            setIsSignUpModalOpen(false);
            if (newUser.featureEntitlements?.includes('build_your_own_trail')) {
              navigateToCustomRoute();
            } else {
              setIsPeakUpsellOpen(true);
            }
          }}
          trigger="custom routes trail page cta tapped"
          upsellTrigger="trail details backpacking banner tapped"
          hideUpsell
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
        />
      )}
      {isPeakUpsellOpen && (
        <GenericPeakUpsell
          isOpen={isPeakUpsellOpen}
          onCloseRequest={() => setIsPeakUpsellOpen(false)}
          onSuccess={() => {
            setIsPeakUpsellOpen(false);
            navigateToCustomRoute();
          }}
          serverUpsellContent={serverUpsellContent}
          upsellTrigger="trail details backpacking banner tapped"
        />
      )}
    </>
  );
};

const BackpackingCtaWrapper = (props: Props) => {
  const isMobile = useIsMobileSizedScreen();

  const experiments = useExperiments();
  const sendExposure = useExposureEvent('web-disco-rm-backpacking-cta');
  const experimentEnabled = experiments['web-disco-rm-backpacking-cta']?.value === 'treatment';

  useEffect(() => {
    if (!isMobile) {
      sendExposure();
    }
  }, [sendExposure, isMobile]);

  if (!experimentEnabled || isMobile) {
    return null;
  }

  return <BackpackingCta {...props} />;
};

export default BackpackingCtaWrapper;
